import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useRoom } from '../hooks/useRoom';
import WaitingRoom, { RoomHeader } from './room/WaitingRoom';
import InGameBar from './room/InGameBar';
import Toasts from './site/Toasts';
import { gameName, SITE_GAMES } from './site/games';
import { rememberRoom } from './site/recentRooms';
import { useLeagueGame, leagueOfRoom, leagueSeats, leagueSideName, leagueGameOfSlug } from './room/leagueRoom';
import type { ParticipantView, RoomView } from '../types/room';
import { act as leagueAct, leagueToken } from '../leagues/api';
import { CTF_ROOM_DEFAULT_CONFIG } from '../games/capture-the-flag/room';
import './site/site.css';
import { ROOM_GAMES } from './room/roomGames';


const PLAYER_NAME_KEY = 'player-name';


const seatNameFor = (room: RoomView) => (i: number, p: ParticipantView) => {
  const lg = leagueOfRoom(room);
  if (lg) return leagueSideName(lg, p);
  return room.gameId === 'capture-the-flag' ? (['Blue', 'Red'][i] ?? `Seat ${i + 1}`) : `Seat ${i + 1}`;
};

// A simple centered message page in the site's style.
function Message({ children }: { children: React.ReactNode }) {
  return (
    <div className="site" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="site-panel" style={{ maxWidth: 420, width: '100%', display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'center' }}>{children}</div>
    </div>
  );
}

// --- RoomPage ---

export default function RoomPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // A league game: the room's name says which; if this browser holds a seat in that league, you join
  // under your league name (so a link copied from someone else's address bar still seats you right).
  const lgSlug = leagueGameOfSlug(slug ?? '');
  const lgId = lgSlug?.leagueId ?? null;
  const lgGame = lgSlug?.gameId ?? null;
  const { info: leagueInfo, problem: leagueProblem, myName: leagueName } = useLeagueGame(lgId, lgGame);
  const [playerName, setPlayerName] = useState(() => localStorage.getItem(PLAYER_NAME_KEY) ?? '');
  const [nameInput, setNameInput] = useState('');
  const [nameReady, setNameReady] = useState(() => !!localStorage.getItem(PLAYER_NAME_KEY));
  const leagueSetup = useRef({ configured: '', bots: new Set<string>(), arranged: '', reported: '' });
  // The first state of the game the host starts (handed to the game, which starts the relay with it).
  const [initialState, setInitialState] = useState<unknown>(null);

  const {
    room, myId, isLeader, error, notices, log, removedBy, takenOver,
    leave, addBot, removeParticipant, setSpectator, setName,
    setGame, arrange, transferLeader, setSeatConfig, suggest, setConfig, setPlaytestMode, start, finish, reportResult,
  } = useRoom(slug!, leagueName === '' ? '' : leagueName ? leagueName : nameReady ? playerName : '');

  // Remember this room for the landing page's "Your rooms".
  useEffect(() => {
    if (!room || !myId || room.slug.startsWith('lg-')) return;   // league games live on the league page
    rememberRoom({ slug: room.slug, gameId: room.gameId, players: room.participants.filter(p => p.id !== myId && p.type !== 'bot').map(p => p.name), at: Date.now() });
  }, [room, myId]);

  // Leaving takes you home (staying on the room page after leaving left you as an invisible watcher).
  const handleLeave = (midGame: boolean) => {
    const msg = midGame
      ? 'Leave this game for now? Your seat is kept: open the room link again to carry on. The others will be told.'
      : 'Leave this room?';
    if (window.confirm(msg)) { leave(); navigate(lgId ? `/league/${lgId}` : '/'); }
  };

  // A new room: the game from the link's ?game=<id>, or the default; either way the server learns how
  // many seats it has.
  useEffect(() => {
    if (!room || !isLeader || room.phase !== 'waiting' || lgId) return;
    const gameParam = searchParams.get('game');
    const wanted = gameParam && SITE_GAMES.some(g => g.id === gameParam) ? gameParam : room.gameId;
    if (room.gameId !== wanted || room.seats == null) {
      setGame(wanted, SITE_GAMES.find(g => g.id === wanted)?.maxPlayers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.slug, isLeader]); // only on initial room join, not every re-render

  // League game, host's browser: set the room up from the league, add a bot for a bot player, and watch
  // if you aren't one of the two players.
  useEffect(() => {
    if (!room || !isLeader || room.phase !== 'waiting' || !leagueInfo) return;
    const cur = leagueOfRoom(room);
    const want = JSON.stringify(leagueInfo);
    if (!cur || JSON.stringify(cur) !== want) {
      if (leagueSetup.current.configured === want) return;   // sent; waiting for the room to update
      const fresh = !cur || cur.gameId !== leagueInfo.gameId || cur.leagueId !== leagueInfo.leagueId;
      leagueSetup.current.configured = want;
      if (fresh) setGame('capture-the-flag', 2);
      setConfig({ ...CTF_ROOM_DEFAULT_CONFIG, ...leagueInfo.options, teamA: leagueInfo.blue.code, teamB: leagueInfo.red.code, abilityA: '', abilityB: '', demo: false, league: leagueInfo } as unknown as Record<string, unknown>);
      return;
    }
    const seats = leagueSeats(room, leagueInfo);
    for (const side of [leagueInfo.blue, leagueInfo.red]) {
      const here = side === leagueInfo.blue ? seats.blue : seats.red;
      if (side.bot && !here && !leagueSetup.current.bots.has(side.id)) { leagueSetup.current.bots.add(side.id); addBot(side.name); }
    }
    // The league's Blue and Red take the seats; anyone else in the room watches.
    const players = [seats.blue, seats.red].filter(Boolean) as ParticipantView[];
    const others = room.participants.filter(p => !players.includes(p));
    const wrong = others.some(p => p.slot !== null) || players.some(p => p.slot === null);
    if (wrong) {
      const key = `${players.map(p => p.id).join()}|${others.map(p => p.id).join()}`;
      if (leagueSetup.current.arranged !== key) {
        leagueSetup.current.arranged = key;
        arrange([...players, ...others].map(p => p.id), others.filter(p => p.type !== 'bot').map(p => p.id));
      }
    }
  }, [room, isLeader, leagueInfo, myId, setGame, setConfig, addBot, arrange]);

  // League game over: report the result to the league (every player's browser tries; the first counts).
  useEffect(() => {
    if (!room?.result || room.leagueNote) return;
    const info = leagueOfRoom(room);
    if (!info || !leagueToken(info.leagueId)) return;
    const key = `${info.gameId}:${room.result.winner}`;
    if (leagueSetup.current.reported === key) return;
    leagueSetup.current.reported = key;
    const w = room.result.winner;
    const winner = w === info.blue.name ? info.blue.id : w === info.red.name ? info.red.id : null;
    if (w && w !== 'Draw' && !winner) return;
    leagueAct(info.leagueId, 'result', { gameId: info.gameId, winner, how: room.result.how }).catch(() => undefined);
  }, [room]);

  const handleNameSubmit = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    localStorage.setItem(PLAYER_NAME_KEY, trimmed);
    setPlayerName(trimmed);
    setNameReady(true);
  };

  const handleStartWithInit = useCallback(() => {
    if (!room) return;
    const players = room.participants
      .filter(p => p.slot !== null)
      .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0))
      .map(p => ({ id: p.id, name: p.name, isBot: p.type === 'bot' }));
    const game = ROOM_GAMES[room.gameId];
    const first = game?.create(room, players, Date.now());
    if (first == null) return;
    setInitialState(first);
    start();
  }, [room, start]);

  // Name entry screen
  if (!nameReady && leagueName === null) {
    return (
      <Message>
        <span className="site-display" style={{ fontSize: 24, color: 'var(--accent)' }}>1001 Game Nights</span>
        <h1 className="site-display" style={{ fontSize: 32, textTransform: 'none' }}>Join the room</h1>
        <p className="site-muted">Your name is shown to the others and remembered in this browser.</p>
        <form onSubmit={e => { e.preventDefault(); handleNameSubmit(); }} style={{ display: 'flex', gap: 8 }}>
          <input value={nameInput} onChange={e => setNameInput(e.target.value)} placeholder="Your name" aria-label="Your name" maxLength={30} autoFocus style={{ flex: 1, minWidth: 0, minHeight: 48, fontSize: 17 }} />
          <button type="submit" className="site-btn site-btn-primary" disabled={!nameInput.trim()} style={{ minHeight: 48 }}>Join</button>
        </form>
      </Message>
    );
  }

  if (error) {
    return <Message><p>Something went wrong: {error}</p><p><button type="button" className="site-btn" onClick={() => navigate('/')}>Back to the home page</button></p></Message>;
  }

  if (takenOver) {
    return (
      <Message>
        <p>This game is now open on another device, so it's no longer played from here.</p>
        <p style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="site-btn site-btn-primary" onClick={() => window.location.reload()}>Play it here instead</button>
          {lgId && <Link className="site-btn" to={`/league/${lgId}`}>Back to the league</Link>}
        </p>
      </Message>
    );
  }

  if (removedBy) {
    return <Message><p>{removedBy} removed you from this room.</p><p><button type="button" className="site-btn" onClick={() => navigate('/')}>Back to the home page</button></p></Message>;
  }

  if (!room || !myId) {
    return <Message><p className="site-muted">Connecting…</p></Message>;
  }

  const me = room.participants.find(p => p.id === myId);
  const rename = (n: string) => { localStorage.setItem(PLAYER_NAME_KEY, n); setName(n); };

  // A league game's room waits for the league (its sides, armies and settings) before showing anything,
  // so it never looks like an ordinary room you could start some other game in.
  if (lgId && room.phase === 'waiting' && !leagueOfRoom(room)) {
    return (
      <Message>
        <span className="site-display" style={{ fontSize: 24, color: 'var(--accent)' }}>League game</span>
        <p>{leagueProblem ?? 'Loading this league game…'}</p>
        <p><Link to={`/league/${lgId}`}>Back to the league</Link></p>
      </Message>
    );
  }

  if (room.phase === 'waiting') {
    return (
      <div className="site">
        <Toasts list={notices} />
        <RoomHeader slug={slug!} myName={me?.name ?? playerName} hostName={isLeader || lgId ? null : room.participants.find(p => p.isLeader)?.name} onHome={() => handleLeave(false)} onRename={rename} />
        <WaitingRoom
          room={room}
          myId={myId}
          isLeader={isLeader}
          minPlayers={ROOM_GAMES[room.gameId]?.minPlayers ?? 1}
          log={log}
          leagueProblem={lgId ? leagueProblem : null}
          actions={{
            setGame, setConfig, setSeatConfig, suggest, arrange, transferLeader, addBot, removeParticipant, setSpectator, setPlaytestMode,
            setName: rename, onStart: handleStartWithInit, onLeave: () => handleLeave(false),
          }}
        />
      </div>
    );
  }

  if (room.phase === 'playing' || room.phase === 'finished') {
    const Game = ROOM_GAMES[room.gameId]?.Component;
    const gameEl = Game
      ? <Game slug={slug!} room={room} myId={myId} isLeader={isLeader} initialState={isLeader ? initialState : null} onFinish={finish} onResult={reportResult} />
      : <div style={{ padding: 40, fontFamily: 'sans-serif' }}>There's no game called "{room.gameId}" on this site.</div>;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <div className="site" style={{ minHeight: 0 }}>
          <Toasts list={notices} />
          <InGameBar
            room={room} myId={myId} isLeader={isLeader} gameName={gameName(room.gameId)} seatName={seatNameFor(room)}
            onHome={() => handleLeave(me?.slot != null && !room.result)}
            onBackToRoom={() => { if (room.result || window.confirm('End this game for everyone and go back to the room?')) finish(); }}
          />
          {room.leagueNote ? (
            <div style={{ background: '#4a2a26', color: '#ffb3a8', padding: '8px 16px', fontSize: 14, textAlign: 'center' }}>
              {room.leagueNote} {lgId && <a href={`/league/${lgId}`} style={{ color: '#ffd1c8' }}>Back to the league</a>}
            </div>
          ) : me && me.slot == null && !room.result && (
            <div style={{ background: '#4a3f22', color: '#f2d48f', padding: '8px 16px', fontSize: 14, textAlign: 'center' }}>
              {lgId ? "You're watching this league game." : me.type === 'spectator' ? "You're watching this game." : "A game is in progress: you're watching, and you'll have a seat in the next game."}
            </div>
          )}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {gameEl}
        </div>
      </div>
    );
  }

  return null;
}
